import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  Link,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HotelIcon from '@mui/icons-material/Hotel';
import PaymentIcon from '@mui/icons-material/Payment';
import { NumericField, OtaFooter, formatDate } from '@ota/design-system';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, type BookingResponse, useAuthSession } from '@ota/api-client';
import { authStore, createBooking, fetchAvailability, fetchGstSlabs, fetchProperty } from '../api';
import { PublicHeader } from '../components/PublicHeader';
import { CancellationPolicyInfo } from '../components/CancellationPolicyInfo';

// Razorpay-checkout typing + script loading + start flow lives in the shared
// useRazorpayCheckout hook (also used by the "Resume payment" button on
// MyBookingsPage).
import { useRazorpayCheckout } from '../hooks/useRazorpayCheckout';

type PaymentMode = 'payNow' | 'payAtHotel';

/**
 * Mirrors the backend GstSlabRepository.findSlabForRate() query:
 *   active=true AND minNightlyRate <= rate AND (maxNightlyRate IS NULL OR maxNightlyRate >= rate)
 *   ORDER BY minNightlyRate DESC — take first result.
 * Falls back to 12% (same default the backend uses) when slabs aren't loaded yet.
 */
function resolveGstPercent(
  slabs: { minNightlyRate: number; maxNightlyRate?: number | null; ratePercent: number }[],
  rate: number,
): number {
  // slabs arrive sorted asc; iterate from the end to hit the highest matching floor first
  for (let i = slabs.length - 1; i >= 0; i--) {
    const s = slabs[i];
    if (s.minNightlyRate <= rate && (s.maxNightlyRate == null || s.maxNightlyRate >= rate)) {
      return s.ratePercent;
    }
  }
  return slabs.length === 0 ? 12 : 0; // 12% fallback when master not loaded; 0 if loaded but no slab matches
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const session = useAuthSession(authStore);

  const propertyId = Number(search.get('propertyId'));
  const roomCategoryId = Number(search.get('roomCategoryId'));
  const checkIn = search.get('checkIn') ?? '';
  const checkOut = search.get('checkOut') ?? '';
  const guests = Number(search.get('guests') ?? '2');
  const mealPlanId = search.get('mealPlanId') ? Number(search.get('mealPlanId')) : undefined;

  // Pre-filled by PropertyDetailPage when the party is bigger than one room's
  // max occupancy; defaults to '1' for direct/legacy links that omit it.
  const [roomsCount, setRoomsCount] = useState(search.get('roomsCount') ?? '1');
  const [specialRequests, setSpecialRequests] = useState('');
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('payNow');
  const [confirmedBooking, setConfirmedBooking] = useState<BookingResponse | null>(null);
  // Razorpay-flow busy state lives in the shared hook.
  const razorpay = useRazorpayCheckout();
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Guest info — seeded from session when present; fully editable for anonymous users.
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  // Contact mobile for this booking — always editable (even when logged in) since
  // the number to reach the guest at may differ from their account phone.
  // Stored/typed as bare 10 digits; "+91" is a fixed display prefix, never sent.
  const [guestMobile, setGuestMobile] = useState('');

  useEffect(() => {
    if (confirmedBooking) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [confirmedBooking]);

  // Sync name/email/mobile when user logs in or the session loads.
  useEffect(() => {
    if (session) {
      setGuestName((prev) => prev || session.user.fullName || '');
      setGuestEmail(session.user.email || '');
      setGuestMobile((prev) => prev || session.user.phone || '');
    }
  }, [session?.user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pay at Hotel requires a signed-in account (server enforces this too — see
  // BookingController.create). Fall back to Pay Now if the session drops out
  // from under an already-selected "payAtHotel" mode (e.g. it expires mid-checkout).
  useEffect(() => {
    if (!session) {
      setPaymentMode((prev) => (prev === 'payAtHotel' ? 'payNow' : prev));
    }
  }, [session]);

  const property = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () => fetchProperty(propertyId),
    enabled: !!propertyId,
  });

  const availability = useQuery({
    queryKey: ['availability', propertyId, checkIn, checkOut],
    queryFn: () => fetchAvailability(propertyId, checkIn, checkOut),
    enabled: !!propertyId && !!checkIn && !!checkOut,
  });

  const gstSlabs = useQuery({
    queryKey: ['gst-slabs'],
    queryFn: fetchGstSlabs,
    staleTime: 10 * 60 * 1000, // slabs change rarely; cache for 10 min
  });

  const category = availability.data?.categories.find((c) => c.roomCategoryId === roomCategoryId);
  const nights = availability.data?.nightsCount ?? 0;
  const selectedPlan = category?.mealPlans.find((p) => p.id === mealPlanId);

  // Safe integer for all calculations — allows the input to be temporarily empty while typing.
  const roomsCountNum = Math.max(1, Number(roomsCount) || 1);

  // Room cost only (no meal supplement)
  const roomOnlyTotal = category ? Number(category.totalForStay) * roomsCountNum : 0;
  // Meal plan supplement total
  const mealPlanTotal =
    selectedPlan && Number(selectedPlan.supplement) > 0
      ? Number(selectedPlan.supplement) * nights * roomsCountNum
      : 0;
  // Combined base (room + meal, before GST)
  const lineTotal = roomOnlyTotal + mealPlanTotal;

  // Dynamic GST — mirrors the backend GstSlab logic exactly.
  // taxApplicable=false → property is GST-exempt; no tax at all.
  // taxApplicable=true + taxInclusive=true → price already includes GST; show "Incl." label.
  // taxApplicable=true + taxInclusive=false → look up the active slab for the per-room nightly rate.
  const taxApplicable = property.data?.taxApplicable ?? true;
  const taxInclusive = property.data?.taxInclusive ?? false;
  const nightlyRatePerRoom =
    nights > 0 && roomsCountNum > 0 ? lineTotal / (nights * roomsCountNum) : 0;
  const gstPercent =
    !taxApplicable || taxInclusive ? 0 : resolveGstPercent(gstSlabs.data ?? [], nightlyRatePerRoom);
  const taxes =
    !taxApplicable || taxInclusive ? 0 : Math.round(lineTotal * (gstPercent / 100) * 100) / 100;
  const grandTotal = lineTotal + taxes;

  // Occupancy check: maxOccupancy is per room, guests is the total party size
  // carried over from the property page. Block checkout if the selected room
  // count can't legally hold the party (mirrors the same check on PropertyDetailPage).
  const roomCapacity = category ? category.maxOccupancy * roomsCountNum : Infinity;
  const occupancyOk = !category || guests <= roomCapacity;

  // Form is valid when name is present, (if anonymous) email looks valid, and the
  // contact mobile is exactly 10 digits starting 6-9 — mirrors the backend's
  // CreateBookingRequest @Pattern("^[6-9]\\d{9}$") so the guest sees the same
  // rule before submitting rather than only after a 400 comes back.
  const emailValid = /\S+@\S+\.\S+/.test(guestEmail);
  const mobileValid = /^[6-9]\d{9}$/.test(guestMobile);
  const isFormValid =
    guestName.trim().length > 0 && (!!session || emailValid) && mobileValid && occupancyOk;

  /** Step 1: create the booking record. */
  const bookMutation = useMutation({
    mutationFn: () =>
      createBooking({
        propertyId,
        roomCategoryId,
        checkIn,
        checkOut,
        guestsCount: guests,
        roomsCount: roomsCountNum,
        mealPlanId,
        payAtHotel: paymentMode === 'payAtHotel',
        guestName: guestName.trim() || undefined,
        // Only pass email for anonymous checkout; server uses authenticated user otherwise.
        guestEmail: !session ? guestEmail.trim() : undefined,
        // Mandatory for every booking, regardless of login state — see CreateBookingRequest.
        guestMobile: guestMobile.trim(),
        // Let the server validate that the price hasn't changed since this page loaded.
        clientTotal: grandTotal,
      }),
    onSuccess: async (booking) => {
      if (paymentMode === 'payAtHotel') {
        setConfirmedBooking(booking);
      } else {
        await openRazorpayModal(booking);
      }
    },
  });

  /** Step 2 (Pay Now only): create Razorpay order then open the modal. */
  async function openRazorpayModal(booking: BookingResponse) {
    setPaymentError(null);
    await razorpay.start(
      booking,
      {
        name: guestName || session?.user.fullName || undefined,
        email: session ? session.user.email : guestEmail,
        contact: session?.user.phone ?? '9999999999',
      },
      {
        onSuccess: (confirmed) => setConfirmedBooking(confirmed),
        onFailure: (msg) => setPaymentError(msg),
        onDismiss: () =>
          setPaymentError('Payment was not completed. You can retry or choose Pay at Hotel.'),
      },
    );
  }

  // Confirmation screen
  if (confirmedBooking) {
    const isPaid = confirmedBooking.paymentStatus === 'PAID';
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <PublicHeader />
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 }, flex: 1 }}>
          <Card>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center', mb: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main' }} />
                <Typography variant="h3">
                  {isPaid ? 'Booking confirmed!' : 'Booking request received!'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Your booking id is <strong>{confirmedBooking.bookingCode}</strong>
                </Typography>
                {isPaid ? (
                  <Alert severity="success" sx={{ width: '100%' }}>
                    Payment of ₹
                    {Number(confirmedBooking.totalAmount).toLocaleString('en-IN', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    captured successfully.
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ width: '100%' }}>
                    You've chosen to pay at the hotel on arrival. The property will confirm your
                    booking shortly. Please carry a valid ID.
                  </Alert>
                )}
              </Stack>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Property
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {confirmedBooking.propertyName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Payment
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {confirmedBooking.paymentStatus}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Check-in
                  </Typography>
                  <Typography variant="body1">{formatDate(confirmedBooking.checkIn)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Check-out
                  </Typography>
                  <Typography variant="body1">{formatDate(confirmedBooking.checkOut)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Nights
                  </Typography>
                  <Typography variant="body1">{confirmedBooking.nightsCount}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Guests
                  </Typography>
                  <Typography variant="body1">{confirmedBooking.guestsCount}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h5">Total</Typography>
                    <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700 }}>
                      ₹
                      {Number(confirmedBooking.totalAmount).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
              <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 4 }}>
                <Button variant="outlined" onClick={() => navigate('/')}>
                  Back to home
                </Button>
                {session && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => navigate('/my-bookings')}
                  >
                    View my bookings
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Container>
        <OtaFooter />
      </Box>
    );
  }

  // Pre-flight guards
  if (!propertyId || !roomCategoryId || !checkIn || !checkOut) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <PublicHeader />
        <Container sx={{ py: 4 }}>
          <Alert severity="error">Missing checkout parameters. Go back and pick a room.</Alert>
        </Container>
      </Box>
    );
  }

  if (checkIn >= checkOut) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <PublicHeader />
        <Container sx={{ py: 4 }}>
          <Alert severity="warning">
            Check-out date must be after check-in date. Please go back and select valid dates.
          </Alert>
        </Container>
      </Box>
    );
  }

  const busy = bookMutation.isPending || razorpay.busy;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, flex: 1 }}>
        {/* Back link — returns to the property detail page with the same
            dates + guests so the user lands on the right availability state
            and can pick a different room or meal plan without losing context. */}
        <Button
          variant="text"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            const qs = new URLSearchParams();
            if (checkIn) qs.set('checkIn', checkIn);
            if (checkOut) qs.set('checkOut', checkOut);
            if (guests) qs.set('guests', String(guests));
            const url = `/hotels/${propertyId}${qs.toString() ? `?${qs.toString()}` : ''}`;
            navigate(url);
          }}
          sx={{ mb: 1.5, alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
        >
          Back to property — change room or dates
        </Button>
        <Typography variant="h3" sx={{ mb: 3 }}>
          Review your booking
        </Typography>

        <Grid container spacing={3}>
          {/* Left: stay + guest details */}
          <Grid item xs={12} md={7}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Stay details
                </Typography>
                {(property.isLoading || availability.isLoading) && <CircularProgress size={24} />}
                {property.data && category && (
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Property
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {property.data.name}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Room category
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {category.name}
                      </Typography>
                    </Stack>
                    {selectedPlan && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Meal plan
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedPlan.code} · {selectedPlan.name}
                          {Number(selectedPlan.supplement) > 0 && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ ml: 0.5, color: 'secondary.main' }}
                            >
                              (+₹
                              {Number(selectedPlan.supplement).toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                              /night)
                            </Typography>
                          )}
                        </Typography>
                      </Stack>
                    )}
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Stay
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(checkIn)} → {formatDate(checkOut)} (
                        {availability.data?.nightsCount} nights)
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Guests
                      </Typography>
                      <Typography variant="body1">{guests}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Rooms
                      </Typography>
                      <NumericField
                        size="small"
                        sx={{ width: 100 }}
                        value={roomsCount}
                        onChange={(e) => setRoomsCount(e.target.value)}
                        onBlur={() => setRoomsCount(String(roomsCountNum))}
                      />
                    </Stack>
                    {category && !occupancyOk && (
                      <Alert severity="warning">
                        {category.maxOccupancy} guest{category.maxOccupancy === 1 ? '' : 's'} max
                        per {category.name} room — {roomsCountNum} room
                        {roomsCountNum === 1 ? '' : 's'} fits up to {roomCapacity}. Increase rooms
                        to fit {guests} guests.
                      </Alert>
                    )}
                  </Stack>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Guest information
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Full name"
                    fullWidth
                    required
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    helperText={session ? 'You can update your name for this booking' : undefined}
                  />
                  <TextField
                    label="Email"
                    fullWidth
                    required
                    type="email"
                    value={guestEmail}
                    disabled={!!session}
                    onChange={(e) => {
                      if (!session) setGuestEmail(e.target.value);
                    }}
                    helperText={
                      !session ? 'Booking confirmation will be sent to this address' : undefined
                    }
                  />
                  <NumericField
                    label="Mobile number"
                    fullWidth
                    required
                    value={guestMobile}
                    onChange={(e) => setGuestMobile(e.target.value.slice(0, 10))}
                    error={guestMobile.length > 0 && !mobileValid}
                    helperText={
                      guestMobile.length > 0 && !mobileValid
                        ? 'Enter a valid 10-digit mobile number'
                        : 'The property will use this to reach you about your booking'
                    }
                    inputProps={{ maxLength: 10 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="span"
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              bgcolor: 'action.disabledBackground',
                              color: 'text.secondary',
                              fontWeight: 600,
                              fontSize: '0.875rem',
                            }}
                          >
                            +91
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="Special requests (optional)"
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Late check-in, dietary preferences, anything else…"
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                  />
                </Stack>
                {!session && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Have an account?{' '}
                    <Link
                      component={RouterLink}
                      to={`/login?returnTo=${encodeURIComponent(`/checkout?${search.toString()}`)}`}
                    >
                      Sign in
                    </Link>{' '}
                    to auto-fill your details and track bookings.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right: price summary + payment mode */}
          <Grid item xs={12} md={5}>
            <Card sx={{ position: 'sticky', top: 88 }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Price summary
                </Typography>

                {category ? (
                  <Stack spacing={2}>
                    {/* Price breakdown */}
                    <Stack spacing={1.5}>
                      {/* Room tariff */}
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack spacing={0}>
                          <Typography variant="body2" color="text.secondary">
                            Room tariff
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {nights} nights × {roomsCountNum} room{roomsCountNum === 1 ? '' : 's'}
                          </Typography>
                        </Stack>
                        <Typography variant="body2">
                          ₹
                          {roomOnlyTotal.toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>
                      </Stack>

                      {/* Meal plan supplement (only when supplement > 0) */}
                      {selectedPlan && Number(selectedPlan.supplement) > 0 && (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Stack spacing={0}>
                            <Typography variant="body2" color="text.secondary">
                              {selectedPlan.code} · {selectedPlan.name}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              +₹{Number(selectedPlan.supplement).toLocaleString('en-IN')}/night ×{' '}
                              {nights} nights × {roomsCountNum} room{roomsCountNum === 1 ? '' : 's'}
                            </Typography>
                          </Stack>
                          <Typography variant="body2">
                            ₹
                            {mealPlanTotal.toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </Typography>
                        </Stack>
                      )}

                      {/* Meal plan included (supplement = 0) */}
                      {selectedPlan && Number(selectedPlan.supplement) === 0 && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            {selectedPlan.code} · {selectedPlan.name}
                          </Typography>
                          <Typography variant="body2" color="success.main">
                            Included
                          </Typography>
                        </Stack>
                      )}

                      {/* GST */}
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack spacing={0}>
                          <Typography variant="body2" color="text.secondary">
                            {!taxApplicable
                              ? 'GST (not applicable)'
                              : taxInclusive
                                ? 'Taxes & fees (included in price)'
                                : gstPercent === 0
                                  ? 'GST (0%)'
                                  : `GST (${gstPercent}%)`}
                          </Typography>
                          {taxApplicable && !taxInclusive && gstPercent > 0 && (
                            <Typography variant="caption" color="text.disabled">
                              On ₹
                              {nightlyRatePerRoom.toLocaleString('en-IN', {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              })}
                              /room/night
                            </Typography>
                          )}
                        </Stack>
                        <Typography variant="body2">
                          {!taxApplicable
                            ? '₹0.00'
                            : taxInclusive
                              ? 'Incl.'
                              : `₹${taxes.toLocaleString('en-IN', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`}
                        </Typography>
                      </Stack>

                      <Divider />

                      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                        <Typography variant="h6">Total</Typography>
                        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700 }}>
                          ₹
                          {grandTotal.toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>
                      </Stack>

                      {/* Cancellation policy — guest sees terms before paying */}
                      <Box sx={{ pt: 1 }}>
                        <CancellationPolicyInfo
                          policyId={property.data?.cancellationPolicyId}
                          variant="compact"
                        />
                      </Box>
                    </Stack>

                    <Divider />

                    {/* Payment mode toggle */}
                    <Stack spacing={1}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: 1,
                        }}
                      >
                        How would you like to pay?
                      </Typography>
                      <ToggleButtonGroup
                        value={paymentMode}
                        exclusive
                        onChange={(_, v) => {
                          if (v) setPaymentMode(v);
                        }}
                        fullWidth
                        size="small"
                        disabled={busy}
                      >
                        <ToggleButton
                          value="payNow"
                          sx={{ flexDirection: 'column', py: 1.5, gap: 0.5 }}
                        >
                          <PaymentIcon fontSize="small" />
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            Pay now
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.65rem' }}
                          >
                            Razorpay · UPI / Card / NB
                          </Typography>
                        </ToggleButton>
                        <ToggleButton
                          value="payAtHotel"
                          disabled={!session}
                          sx={{ flexDirection: 'column', py: 1.5, gap: 0.5 }}
                        >
                          <HotelIcon fontSize="small" />
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            Pay at hotel
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.65rem' }}
                          >
                            {session ? 'Cash / card on arrival' : 'Sign in required'}
                          </Typography>
                        </ToggleButton>
                      </ToggleButtonGroup>

                      {!session && (
                        <Alert severity="info" sx={{ py: 0.5 }}>
                          <Link
                            component={RouterLink}
                            to={`/login?returnTo=${encodeURIComponent(`/checkout?${search.toString()}`)}`}
                          >
                            Sign in
                          </Link>{' '}
                          to unlock Pay at Hotel. Guest checkout requires online payment.
                        </Alert>
                      )}
                      {paymentMode === 'payAtHotel' && (
                        <Alert severity="info" sx={{ py: 0.5 }}>
                          Your booking request is sent to the property. Payment is collected at
                          check-in after the hotelier confirms.
                        </Alert>
                      )}
                      {paymentMode === 'payNow' && (
                        <Alert severity="success" sx={{ py: 0.5 }}>
                          Secure online payment via Razorpay. UPI, Debit/Credit card, Net banking
                          accepted.
                        </Alert>
                      )}
                    </Stack>

                    {/* Errors */}
                    {bookMutation.isError && (
                      <Alert severity="error">
                        {bookMutation.error instanceof ApiError
                          ? (bookMutation.error.body?.message ?? bookMutation.error.message)
                          : 'Booking failed'}
                      </Alert>
                    )}
                    {paymentError && (
                      <Alert severity="error" onClose={() => setPaymentError(null)}>
                        {paymentError}
                      </Alert>
                    )}

                    {/* CTA */}
                    <Button
                      variant="contained"
                      color="secondary"
                      size="large"
                      disabled={
                        busy || (category?.remainingCount ?? 0) < roomsCountNum || !isFormValid
                      }
                      onClick={() => bookMutation.mutate()}
                      startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                      {busy
                        ? razorpay.busy
                          ? 'Processing payment…'
                          : 'Creating booking…'
                        : paymentMode === 'payNow'
                          ? `Pay ₹${grandTotal.toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          : 'Confirm booking'}
                    </Button>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textAlign: 'center' }}
                    >
                      {paymentMode === 'payNow'
                        ? 'You will be redirected to Razorpay to complete payment securely.'
                        : 'No payment collected online. Amount is due at the property on check-in.'}
                    </Typography>
                  </Stack>
                ) : (
                  <CircularProgress size={24} />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Full-screen loader — blocks all interaction while the booking/payment API is running */}
      <Backdrop
        open={busy}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          color: '#fff',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" size={56} />
        <Typography variant="h6" sx={{ color: '#fff' }}>
          {razorpay.busy ? 'Processing payment…' : 'Creating your booking…'}
        </Typography>
      </Backdrop>

      <OtaFooter />
    </Box>
  );
}
